/**
 * Global Auth Modal context.
 *
 * Lets any component (header CTAs, hero, footer, paywall, etc.) pop the
 * dashboard sign-in / sign-up flow as a modal instead of navigating to a
 * full-page route. Mounted once at the App root.
 *
 * Usage:
 *   const { open } = useAuthModal();
 *   <button onClick={() => open('signin')}>Sign in</button>
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const AuthModalContext = createContext(null);

export const AuthModalProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, mode: 'signin', redirectTo: null, returnY: null });

  const open = useCallback((mode = 'signin', opts = {}) => {
    // `opts.returnY` is the scroll position captured by the trigger on
    // pointerdown (before the browser's focus default scrolls the page).
    // Falls back to current scrollY for triggers that don't capture.
    const returnY = (typeof opts.returnY === 'number')
      ? opts.returnY
      : (typeof window !== 'undefined' ? window.scrollY : null);
    setState({ open: true, mode, redirectTo: opts.redirectTo || null, returnY });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  const setMode = useCallback((mode) => setState((s) => ({ ...s, mode })), []);

  const value = useMemo(
    () => ({
      isOpen: state.open,
      mode: state.mode,
      redirectTo: state.redirectTo,
      returnY: state.returnY,
      open,
      close,
      setMode,
    }),
    [state, open, close, setMode],
  );

  return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>;
};

export const useAuthModal = () => {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    return {
      isOpen: false,
      mode: 'signin',
      redirectTo: null,
      returnY: null,
      open: () => {},
      close: () => {},
      setMode: () => {},
    };
  }
  return ctx;
};
